import { useState } from 'react';
import { Modal, ModalBody, Button, Stack } from '@7shifts/sous-chef-base';
import type {
    StatusShift,
    ClockRole,
    UserStatusAction,
    PunchResponse
} from '@src/api/punchTypes';
import { useTranslation } from '@src/i18n';

interface StartShiftRoleModalProps {
    shift: StatusShift;
    otherRoles: ClockRole[];
    action: UserStatusAction;
    onAction: (execute: () => Promise<PunchResponse>) => void;
    onDismiss: () => void;
}

export function StartShiftRoleModal({
    shift,
    otherRoles,
    action,
    onAction,
    onDismiss
}: StartShiftRoleModalProps) {
    const [submitted, setSubmitted] = useState(false);
    const { t } = useTranslation();

    function handleScheduledRole() {
        if (submitted) return;
        setSubmitted(true);
        onAction(action.execute);
    }

    function handleOtherRole(roleId: number) {
        if (submitted || !action.executeWithRole) return;
        setSubmitted(true);
        onAction(() => action.executeWithRole!(roleId));
    }

    return (
        <Modal header={t('role.confirmTitle')} testId="start-shift-role-modal">
            <ModalBody>
                <Stack space={8}>
                    <Button
                        type="button"
                        theme="primary"
                        size="full-width"
                        disabled={submitted}
                        loading={submitted}
                        onClick={handleScheduledRole}
                        testId="start-shift-role-scheduled"
                    >
                        <span className="punch-pad__role-option">
                            {shift.color && (
                                <span
                                    className="punch-pad__role-color"
                                    style={{ backgroundColor: shift.color }}
                                />
                            )}
                            {shift.roleName}
                        </span>
                    </Button>
                    {otherRoles.map(role => (
                        <Button
                            key={role.id}
                            type="button"
                            theme="hollow"
                            size="full-width"
                            disabled={submitted}
                            loading={submitted}
                            onClick={() => handleOtherRole(role.id)}
                            testId={`start-shift-role-other-${role.id}`}
                        >
                            <span className="punch-pad__role-option">
                                {role.color && (
                                    <span
                                        className="punch-pad__role-color"
                                        style={{ backgroundColor: role.color }}
                                    />
                                )}
                                {role.name}
                            </span>
                        </Button>
                    ))}
                    <Button
                        type="button"
                        theme="hollow"
                        size="full-width"
                        disabled={submitted}
                        onClick={onDismiss}
                        testId="start-shift-role-cancel"
                    >
                        {t('common.cancel')}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
